import React, { Children, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { useCampaign } from "@/hooks/campaign";
import { cn } from "@/lib/utils";
import { ExternalLink, Link as LinkIcon } from "lucide-react";
import Image from "next/image";

interface HubUpdatesLinkProps {
  href?: string;
  children: React.ReactNode;
}

const getLinkMeta = (href?: string) => {
  if (!href) {
    return { isCedeHub: false, campaignName: null as string | null, segments: [] as string[] };
  }

  try {
    const url = new URL(href);
    const isCedeHub = url.hostname.includes("cedehub.io");
    if (!isCedeHub) return { isCedeHub: false, campaignName: null, segments: [] };

    const segments = url.pathname.split("/").filter(Boolean);
    const campaignName = segments[0] || null;

    return { isCedeHub, campaignName, segments };
  } catch (error) {
    return { isCedeHub: false, campaignName: null, segments: [] };
  }
};

const useHubUpdatesLink = ({ href, children }: HubUpdatesLinkProps) => {
  const childArray = Children.toArray(children);
  const labelText = childArray
    .map((child) => (typeof child === "string" ? child : ""))
    .join(" ")
    .trim();
  const hasCustomLabel =
    childArray.length > 0 && childArray.some((child) => (typeof child === "string" ? child.trim().length > 0 : true));
  const labelMentionsCede = labelText.toLowerCase().includes("cede");

  const { isCedeHub, campaignName, segments } = useMemo(() => getLinkMeta(href), [href]);

  const shouldRenderHubButton = isCedeHub && (!hasCustomLabel || labelMentionsCede || labelText.length === 0);

  return {
    campaignName,
    shouldRenderHubButton,
    labelText,
    segments,
  };
};

export const HubUpdatesLink = ({ href, children }: HubUpdatesLinkProps) => {
  const { campaignName, shouldRenderHubButton, labelText, segments } = useHubUpdatesLink({ href, children });
  const { campaign } = useCampaign(shouldRenderHubButton ? campaignName || undefined : undefined);

  const openHref = () => {
    if (href) {
      window.open(href, "_blank", "noopener,noreferrer");
    }
  };

  const formatSegmentsLabel = () => {
    if (!segments.length) return "";
    return segments
      .map((segment) =>
        segment
          .split(/[-_]/)
          .filter(Boolean)
          .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
          .join(" "),
      )
      .join(" ");
  };

  if (!shouldRenderHubButton) {
    return (
      <a
        href={href}
        className="inline-flex items-center gap-1.5 text-cede-primary font-semibold underline underline-offset-4 decoration-cede-primary/40 hover:text-cede-primary-dark transition-colors"
        target="_blank"
        rel="noopener noreferrer"
      >
        <span>{children}</span>
        <LinkIcon className="h-3 w-3" />
      </a>
    );
  }

  const formattedPathLabel = formatSegmentsLabel() || "Cede Hub";
  const label = campaign?.title || campaign?.name || formattedPathLabel || labelText || "View campaign";
  const leftIcon = campaign?.icon ? (
    <Image src={campaign.icon} alt="" width={20} height={20} className="rounded-full object-contain" />
  ) : null;

  return (
    <div className="mt-2 w-full flex justify-center">
      <Button
        size="medium"
        variant="secondary"
        className={cn(
          "inline-flex min-w-[250px] items-center gap-2 px-3 py-2 rounded-button",
          "bg-cede-bg-component border border-cede-border-container",
          "hover:bg-cede-bg-component/50",
          "active:bg-cede-bg-component/50 transition-all",
        )}
        onClick={openHref}
      >
        {leftIcon && (
          <span className="h-6 w-6 rounded-full bg-cede-bg-component/60 flex items-center justify-center overflow-hidden ring-1 ring-cede-border-container">
            {leftIcon}
          </span>
        )}
        <span className="text-cede-primary font-semibold whitespace-nowrap">{label}</span>
        <ExternalLink className="h-4 w-4 text-cede-primary shrink-0" />
      </Button>
    </div>
  );
};
